/** biome-ignore-all lint/correctness/useExhaustiveDependencies: TODO: fix */

import { useEffect, useMemo, useRef } from 'react';
import Files from 'react-files';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { Col, Form, Row } from 'antd';
import { cloneDeep } from 'es-toolkit';

import { convertBlobToBase64 } from '@/utils/file';
import { useFocusableRef } from '@/views/screens/Claims/common/hooks/useFocusableRef';

import { routeFileSelector } from '../../../../../../utilz/i18n';
import Button from '../../../../../components/Buttons/Button';
import SimpleFileList from '../../../../../components/Cards/SimpleFileList';
import FormTextArea from '../../../../../components/Inputs/FormTextArea';
import HiddenFormInput from '../../../../../components/Inputs/HiddenFormInput';
import PrimaryLabel from '../../../../../components/Labels/PrimaryLabel';
import Paper from '../../../../../components/Paper/Paper';
import SaveCancelButtons from '../../../../Shipment/components/SaveCancelButtons';
import { PreClaimDocumentTypeSelect } from '../../../PreClaims';

const LADClaimsFilesCard = ({
  editMode = false,
  isLastItem = false,
  shadowVisible = true,
  index = 0,
  fileDetails,
  setEditMode,
  handleCancel,
  handleSubmit,
  handleDelete,
  fileParamName = 'fileContent',
}) => {
  const { pathname: activeTab } = useLocation();
  const namespace = useMemo(() => routeFileSelector(activeTab), [activeTab]);
  const { t } = useTranslation([namespace, 'translation']);
  const [form] = Form.useForm();
  const fileList = Form.useWatch('fileList', form);
  const cardRef = useFocusableRef('.files-dropzone button', {
    isEnabled: fileDetails?.isNew && !fileDetails?.isInitial,
  });
  const deletedFilesRef = useRef([]);

  useEffect(() => {
    if (fileDetails && !editMode) {
      deletedFilesRef.current = [];
      form.setFieldsValue(fileDetails);
    }
  }, [fileDetails, editMode, form]);

  const convertFileToBase64 = (file, defaultData) =>
    new Promise((resolve, reject) =>
      (async () => {
        try {
          const base64 = await convertBlobToBase64(file);
          const data = {
            dirtyFlag: defaultData.dirtyFlag,
            [fileParamName]: base64.split(',')[1],
            fileName: file.name,
            sequence: defaultData.sequence,
          };
          resolve(data);
        } catch (error) {
          reject(error);
          console.log(error);
        }
      })(),
    );

  const handleChooseFile = async (selectedFiles) => {
    try {
      let fileListL = cloneDeep(fileList ? fileList : []);
      let newFiles = selectedFiles.map((row) => convertFileToBase64(row, { dirtyFlag: 'i', sequence: 0 }));
      newFiles = await Promise.all(newFiles);
      fileListL = [...fileListL, ...newFiles];
      form.setFieldValue('fileList', fileListL);
    } catch (error) {
      console.log(error);
    }
  };

  const handleFileDelete = (index) => {
    const fileListL = cloneDeep(fileList ? fileList : []);
    const deletedFile = fileListL[index];

    if (deletedFile.id) {
      deletedFile.dirtyFlag = 'D';
      deletedFilesRef.current.push(deletedFile);
    }

    fileListL.splice(index, 1);
    form.setFieldValue('fileList', fileListL);
  };

  const onChangeComments = (data) => {
    form.setFieldValue('fileComments', data?.target?.value);
    form.setFieldValue(
      'fileList',
      fileList?.map((file) => ({
        ...file,
        dirtyFlag: file.dirtyFlag === 'N' ? 'U' : file.dirtyFlag,
        fileComments: data?.target?.value,
      })),
    );
  };

  const onFormFinish = (data) => {
    const updatedFileList = (data.fileList || []).map((file) => ({
      ...file,
      dirtyFlag: !fileDetails?.isNew && (file.dirtyFlag === 'N' || !file.dirtyFlag) ? 'U' : file.dirtyFlag || 'N',
    }));
    const submissionData = {
      ...data,
      fileList: [...updatedFileList, ...deletedFilesRef.current],
    };
    deletedFilesRef.current = [];
    handleSubmit(submissionData, index);
  };

  const onCancel = () => {
    deletedFilesRef.current = [];
    handleCancel(index, fileDetails.isNew);
  };

  return (
    <div ref={cardRef}>
      <Paper className={`border-muted ${!isLastItem ? 'mb-16' : ''} ${shadowVisible ? '' : 'shadow-none'}`}>
        <Form onFinish={onFormFinish} initialValues={fileDetails} form={form}>
          <div className="border-bottom-muted pt-8 pb-10 px-16">
            <Row justify="space-between align-items-center">
              <Col className="flex-1" align="middle">
                <Row gutter={10} align="middle">
                  <Col>
                    <label className="mandatory-label">
                      File Name: <span className="mandatory-field">*</span>
                    </label>
                  </Col>
                  <Col>
                    {editMode ? (
                      <Files
                        className="files-dropzone"
                        onChange={handleChooseFile}
                        accepts={['.xlsx', '.xls', 'image/*', '.doc', '.docx', '.ppt', '.pptx', '.txt', '.pdf']}
                        multiple
                        minFileSize={0}
                        clickable={editMode}
                      >
                        <Button disable>{t('translation:default.documentManager.chooseFile')}</Button>
                      </Files>
                    ) : null}

                    <HiddenFormInput
                      name="fileList"
                      rules={[
                        {
                          message: t('translation:default.documentManager.chooseFileMessage'),
                          required: true,
                        },
                      ]}
                    />
                  </Col>
                </Row>
              </Col>

              <Col>
                <SaveCancelButtons
                  editMode={editMode}
                  lightMode={false}
                  setEditMode={(val) => setEditMode(index, val)}
                  handleRemove={() => handleDelete(index)}
                  isNew={fileDetails.isNew}
                  idSuffix={`product-details-${index + 1}`}
                  isModified
                  onCancel={onCancel}
                />
              </Col>
            </Row>
          </div>
          <div className="px-15 pt-10">
            <Row gutter={15}>
              {/* {fileList?.length > 0 ? ( */}
              <Col sm={8} className="pb-10">
                <PrimaryLabel className="mb-5">Selected Files:</PrimaryLabel>
                <div>
                  {fileList?.length > 0 &&
                    fileList.map((file, index) => (
                      <SimpleFileList
                        index={index}
                        key={file.id}
                        data={file}
                        handleDelete={() => handleFileDelete(index)}
                        showDeleteButton={editMode}
                      />
                    ))}
                </div>
              </Col>

              <Col sm={8}>
                <PreClaimDocumentTypeSelect
                  label="Document Type"
                  placeholder="Select Document Type"
                  editMode={editMode}
                  name="documentType"
                  rules={[{ message: 'Please choose a Document Type', required: true }]}
                />
              </Col>
              <Col sm={8}>
                <FormTextArea
                  label={t('translation:claims.comments')}
                  placeholder={t('translation:claims.enterComments')}
                  editMode={editMode}
                  {...(editMode ? { rows: 3 } : {})}
                  name="fileComments"
                  maxLength={640}
                  onChange={onChangeComments}
                />
              </Col>
            </Row>
          </div>
          <HiddenFormInput name="sequence" />
          <HiddenFormInput name="isNew" />
          <HiddenFormInput name="editMode" />
          <HiddenFormInput name="claimKey" />
          <HiddenFormInput name="dirtyFlag" />
        </Form>
      </Paper>
    </div>
  );
};

export default LADClaimsFilesCard;
